import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// 로그인 체크 제거됨

interface LearningMaterial {
  id: string;
  title: string;
  subject: string;
  grade: string;
  description: string;
  author: string;
  category: string;
  fileType: string;
  fileSize: string;
  uploadDate: string;
  downloadCount: number;
  tags: string[];
  materials: string[];
  status?: string;
}

interface StudyGuide {
  id: string;
  title: string;
  materialId: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  tags: string[];
}

const StudentClassroom: React.FC = () => {
  const navigate = useNavigate();
  // 로그인 체크 제거됨

  const [materials, setMaterials] = useState<LearningMaterial[]>([]);
  const [studyGuides, setStudyGuides] = useState<StudyGuide[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    subject: 'technology',
    grade: 'middle',
    description: '',
    author: '',
    category: '',
    fileType: 'pdf',
    tags: ''
  });

  // 샘플 데이터
  const sampleMaterials: LearningMaterial[] = [
    {
      id: '1',
      title: 'MakeCode Arcade 게임 개발 가이드',
      subject: 'technology',
      grade: 'middle',
      description: 'MakeCode Arcade를 활용한 게임 개발의 기초를 학습할 수 있는 자료입니다.',
      author: '김게임',
      category: '프로그래밍',
      fileType: 'pdf',
      fileSize: '2.5MB',
      uploadDate: '2024-01-15',
      downloadCount: 156,
      tags: ['MakeCode', '게임개발', '기초'],
      materials: ['게임개발가이드.pdf', '실습예제.zip']
    },
    {
      id: '3',
      title: '3D 모델링 기초 학습 자료',
      subject: 'technology',
      grade: 'middle',
      description: '3D 모델링 소프트웨어 사용법을 기초부터 학습할 수 있는 자료입니다.',
      author: '박3D',
      category: '3D모델링',
      fileType: 'pdf',
      fileSize: '1.8MB',
      uploadDate: '2024-02-01',
      downloadCount: 234,
      tags: ['3D모델링', 'Tinkercad', '기초'],
      materials: ['3D모델링기초.pdf']
    }
  ];

  const sampleStudyGuides: StudyGuide[] = [
    {
      id: '1',
      title: 'MakeCode Arcade 첫 번째 게임 만들기',
      materialId: '1',
      description: 'MakeCode Arcade를 활용하여 간단한 게임을 만드는 단계별 가이드입니다.',
      difficulty: 'beginner',
      estimatedTime: '2시간',
      tags: ['MakeCode', '게임제작', '기초']
    },
    {
      id: '3',
      title: '3D 모델링 실습 가이드',
      materialId: '3',
      description: 'Tinkercad를 활용하여 간단한 3D 모델을 만드는 방법을 안내합니다.',
      difficulty: 'beginner',
      estimatedTime: '1.5시간',
      tags: ['3D모델링', 'Tinkercad', '실습']
    }
  ];

  useEffect(() => {
    setMaterials(sampleMaterials);
    setStudyGuides(sampleStudyGuides);
  }, []);

  const subjects = [
    { id: 'technology', name: '기술' },
    { id: 'computer', name: '정보' },
    { id: 'science', name: '과학' },
    { id: 'math', name: '수학' }
  ];

  const grades = [
    { id: 'elementary', name: '초등' },
    { id: 'middle', name: '중등' },
    { id: 'high', name: '고등' }
  ];

  const categories = [
    { id: 'programming', name: '프로그래밍' },
    { id: 'robotics', name: '로봇공학' },
    { id: '3dmodeling', name: '3D모델링' },
    { id: 'electronics', name: '전자공학' }
  ];

  const tabLabels = ['학습 자료', '학습 가이드', '최근 업로드'];

  const getFilteredMaterials = () => {
    let filtered = materials;

    // 탭별 필터링
    switch (selectedTab) {
      case 0: // 학습 자료
        filtered = filtered;
        break;
      case 2: // 최근 업로드 (최근 30일)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        filtered = filtered.filter(material => 
          new Date(material.uploadDate) >= thirtyDaysAgo
        );
        break;
    }

    // 교과 필터링
    if (selectedSubject !== 'all') {
      filtered = filtered.filter(material => material.subject === selectedSubject);
    }

    // 검색어 필터링
    if (searchTerm) {
      filtered = filtered.filter(material =>
        material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const getFilteredStudyGuides = () => {
    if (selectedTab !== 1) return [];
    
    let filtered = studyGuides;

    // 교과 필터링
    if (selectedSubject !== 'all') {
      filtered = filtered.filter(guide => {
        const material = materials.find(m => m.id === guide.materialId);
        return material && material.subject === selectedSubject;
      });
    }

    // 검색어 필터링
    if (searchTerm) {
      filtered = filtered.filter(guide =>
        guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const handleCreateMaterial = () => {
    const newMaterialData: LearningMaterial = {
      id: Date.now().toString(),
      title: newMaterial.title,
      subject: newMaterial.subject,
      grade: newMaterial.grade,
      description: newMaterial.description,
      author: newMaterial.author,
      category: newMaterial.category,
      fileType: newMaterial.fileType,
      fileSize: '0MB',
      uploadDate: new Date().toISOString().split('T')[0],
      downloadCount: 0,
      tags: newMaterial.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      materials: []
    };

    setMaterials([...materials, newMaterialData]);
    setOpenDialog(false);
    setNewMaterial({
      title: '',
      subject: 'technology',
      grade: 'middle',
      description: '',
      author: '',
      category: '',
      fileType: 'pdf',
      tags: ''
    });
  };

  const getSubjectName = (subjectId: string) => {
    return subjects.find(sub => sub.id === subjectId)?.name || subjectId;
  };

  const getGradeName = (gradeId: string) => {
    return grades.find(grade => grade.id === gradeId)?.name || gradeId;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enrolled': return 'primary';
      case 'completed': return 'success';
      case 'available': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'enrolled': return '수강 중';
      case 'completed': return '완료';
      case 'available': return '신청 가능';
      default: return status;
    }
  };

  const getAssignmentStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'submitted': return 'info';
      case 'graded': return 'success';
      default: return 'default';
    }
  };

  const getAssignmentStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '제출 대기';
      case 'submitted': return '제출 완료';
      case 'graded': return '채점 완료';
      default: return status;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'default';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return '초급';
      case 'intermediate': return '중급';
      case 'advanced': return '고급';
      default: return difficulty;
    }
  };

  const filteredMaterials = getFilteredMaterials();
  const filteredStudyGuides = getFilteredStudyGuides();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
          color: 'white',
          padding: '2rem 0'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
            <button
              onClick={() => navigate('/notes')}
              style={{
                color: 'white',
                marginBottom: '1rem',
                padding: '0.5rem 1rem',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ← 뒤로 가기
            </button>
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem' }}>
            학생 학습 자료실
          </h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
            학생들을 위한 다양한 학습 자료와 가이드를 확인하세요
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        {/* 탭 네비게이션 */}
        <div style={{ backgroundColor: 'white', marginBottom: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex' }}>
            {tabLabels.map((label, index) => (
              <button
                key={index}
                onClick={() => setSelectedTab(index)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  border: 'none',
                  backgroundColor: selectedTab === index ? '#4CAF50' : 'transparent',
                  color: selectedTab === index ? 'white' : '#333',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  borderBottom: selectedTab === index ? '3px solid #388E3C' : '3px solid transparent'
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 필터 및 검색 */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', marginBottom: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <input
                type="text"
                placeholder={selectedTab === 1 ? "학습 가이드 검색..." : "학습 자료 검색..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#666' }}>🔍</span>
            </div>
            
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              style={{
                minWidth: '120px',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">전체</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 학습 자료 목록 (탭 0, 2) */}
        {(selectedTab === 0 || selectedTab === 2) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredMaterials.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '3rem', textAlign: 'center', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '4rem', color: '#666', marginBottom: '1rem' }}>📚</div>
                <h3 style={{ color: '#666', marginBottom: '0.5rem' }}>
                  해당하는 학습 자료가 없습니다
                </h3>
                <p style={{ color: '#666' }}>
                  다른 검색어나 필터를 시도해보세요
                </p>
              </div>
            ) : (
              filteredMaterials.map((material) => (
                <div key={material.id} style={{ backgroundColor: 'white', marginBottom: '1rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <h3 style={{ marginRight: '1rem', margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                            {material.title}
                          </h3>
                          <span style={{ 
                            backgroundColor: '#4CAF50', 
                            color: 'white', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {material.fileType.toUpperCase()}
                          </span>
                        </div>
                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                          {material.description}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                      <span style={{ 
                        backgroundColor: '#4CAF50', 
                        color: 'white', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem'
                      }}>
                        {getSubjectName(material.subject)}
                      </span>
                      <span style={{ 
                        border: '1px solid #ddd', 
                        color: '#333', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem'
                      }}>
                        {getGradeName(material.grade)}
                      </span>
                      <span style={{ 
                        border: '1px solid #ddd', 
                        color: '#333', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem'
                      }}>
                        작성자: {material.author}
                      </span>
                      <span style={{ 
                        border: '1px solid #ddd', 
                        color: '#333', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem'
                      }}>
                        {material.fileSize}
                      </span>
                      <span style={{ 
                        border: '1px solid #ddd', 
                        color: '#333', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem'
                      }}>
                        다운로드: {material.downloadCount}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '1rem' }}>
                      {material.tags.map((tag, index) => (
                        <span 
                          key={index} 
                          style={{ 
                            border: '1px solid #ddd', 
                            color: '#333', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      onClick={() => console.log('자료 다운로드:', material.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      📥 다운로드
                    </button>
                    <button 
                      onClick={() => console.log('자료 상세:', material.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      👁️ 상세 보기
                    </button>
                    {material.status === 'available' && (
                      <button 
                        onClick={() => console.log('강의 신청:', material.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        🎓 신청하기
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 학습 가이드 목록 (탭 1) */}
        {selectedTab === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredStudyGuides.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '3rem', textAlign: 'center', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '4rem', color: '#666', marginBottom: '1rem' }}>📄</div>
                <h3 style={{ color: '#666', marginBottom: '0.5rem' }}>
                  해당하는 학습 가이드가 없습니다
                </h3>
                <p style={{ color: '#666' }}>
                  다른 검색어나 필터를 시도해보세요
                </p>
              </div>
            ) : (
              <div>
                {filteredStudyGuides.map((guide) => (
                  <div key={guide.id} style={{ backgroundColor: 'white', marginBottom: '1rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        backgroundColor: '#4CAF50', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem'
                      }}>
                        📄
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                            {guide.title}
                          </h3>
                          <span style={{ 
                            backgroundColor: getDifficultyColor(guide.difficulty) === 'success' ? '#4CAF50' : 
                                           getDifficultyColor(guide.difficulty) === 'warning' ? '#FF9800' : '#F44336',
                            color: 'white', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem'
                          }}>
                            {getDifficultyText(guide.difficulty)}
                          </span>
                        </div>
                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                          {guide.description}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{ 
                            border: '1px solid #ddd', 
                            color: '#333', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem'
                          }}>
                            ⏰ 예상 시간: {guide.estimatedTime}
                          </span>
                          <span style={{ 
                            border: '1px solid #ddd', 
                            color: '#333', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem'
                          }}>
                            🎓 난이도: {getDifficultyText(guide.difficulty)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {guide.tags.map((tag, index) => (
                            <span 
                              key={index} 
                              style={{ 
                                border: '1px solid #ddd', 
                                color: '#333', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '4px', 
                                fontSize: '0.75rem'
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          onClick={() => console.log('가이드 상세:', guide.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          👁️ 상세 보기
                        </button>
                        <button 
                          onClick={() => console.log('가이드 시작:', guide.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          ▶️ 학습 시작
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 새 자료 등록 버튼 */}
        {selectedTab === 0 && (
          <button
            onClick={() => setOpenDialog(true)}
            style={{
              position: 'fixed',
              bottom: '1rem',
              right: '1rem',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              zIndex: 1000
            }}
          >
            +
          </button>
        )}

        {/* 새 자료 등록 다이얼로그 */}
        {openDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>새 학습 자료 등록</h2>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>자료 제목</label>
                <input
                  type="text"
                  value={newMaterial.title}
                  onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>교과</label>
                  <select
                    value={newMaterial.subject}
                    onChange={(e) => setNewMaterial({ ...newMaterial, subject: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>학년</label>
                  <select
                    value={newMaterial.grade}
                    onChange={(e) => setNewMaterial({ ...newMaterial, grade: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>작성자</label>
                  <input
                    type="text"
                    value={newMaterial.author}
                    onChange={(e) => setNewMaterial({ ...newMaterial, author: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>카테고리</label>
                  <select
                    value={newMaterial.category}
                    onChange={(e) => setNewMaterial({ ...newMaterial, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>파일 형식</label>
                <select
                  value={newMaterial.fileType}
                  onChange={(e) => setNewMaterial({ ...newMaterial, fileType: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="pdf">PDF</option>
                  <option value="doc">DOC</option>
                  <option value="ppt">PPT</option>
                  <option value="zip">ZIP</option>
                  <option value="video">VIDEO</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>자료 설명</label>
                <textarea
                  value={newMaterial.description}
                  onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>태그 (쉼표로 구분)</label>
                <input
                  type="text"
                  value={newMaterial.tags}
                  onChange={(e) => setNewMaterial({ ...newMaterial, tags: e.target.value })}
                  placeholder="예: MakeCode, 게임개발, 기초"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setOpenDialog(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  취소
                </button>
                <button
                  onClick={handleCreateMaterial}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  등록
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentClassroom;

